/**
 * Display formatting for ETF rows. Display only: nothing here rounds a value
 * that is used for anything else. Handover Part B §B7.
 */

const DASH = "—";

/** Two-decimal percent, "—" for null. */
export const formatEtfPercent = (value: number | null | undefined, digits = 2): string =>
  typeof value === "number" && Number.isFinite(value)
    ? `${value.toLocaleString("en-US", {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
      })}%`
    : DASH;

/**
 * Expense ratio is already a percent (0.45 → "0.45%"), never × 100. Null is
 * "—": a zero-fee fund also stores null, so "no fee" and "unknown" look alike.
 */
export const formatExpenseRatio = (value: number | null | undefined): string =>
  formatEtfPercent(value, 2);

const compact = (value: number): string => {
  const abs = Math.abs(value);
  if (abs >= 1e12) return `${(value / 1e12).toFixed(2)}T`;
  if (abs >= 1e9) return `${(value / 1e9).toFixed(2)}B`;
  if (abs >= 1e6) return `${(value / 1e6).toFixed(2)}M`;
  if (abs >= 1e3) return `${(value / 1e3).toFixed(2)}K`;
  return value.toFixed(0);
};

/**
 * AUM in its own reporting currency. GBp is pence: divided by 100 and shown
 * as GBP. Normalising GBp to GBP without dividing overstates those funds 100×.
 */
export const formatAum = (
  aum: number | null | undefined,
  currency: string | null | undefined,
): string => {
  if (typeof aum !== "number" || !Number.isFinite(aum)) return DASH;
  const cur = (currency ?? "").trim();
  const value = cur === "GBp" ? aum / 100 : aum;
  const code = cur === "GBp" ? "GBP" : cur;
  return code ? `${compact(value)} ${code}` : compact(value);
};

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/**
 * A naive date or date-time ("2026-10-06", "2026-10-13T12:44:36") as
 * "6 Oct 2026". String surgery only: these carry no zone, and parsing them
 * with Date can shift the day.
 */
export const formatNaiveDate = (value: string | null | undefined): string => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value ?? "");
  if (!match) return DASH;
  const month = MONTHS[Number(match[2]) - 1];
  if (!month) return DASH;
  return `${Number(match[3])} ${month} ${match[1]}`;
};

export const formatCount = (value: number | null | undefined): string =>
  typeof value === "number" && Number.isFinite(value)
    ? value.toLocaleString("en-US", { maximumFractionDigits: 0 })
    : DASH;
